<template>
  <div>
    <b-table
      :items="lines"
      :fields="lineFields"
      :tbody-tr-class="tbodyTrClass"
      :per-page="perPage"
      :current-page="currentPage"
      bordered
      small
      show-empty
      empty-text="Không có dòng nào"
      class="mb-1"
    >
      <template #head(store)>
        Cửa hàng <span class="text-danger">*</span>
      </template>
      <template #head(costItem)>
        Khoản mục phí <span class="text-danger">*</span>
      </template>
      <template #head(requestedAmount)>
        Số tiền (VNĐ) <span class="text-danger">*</span>
      </template>

      <template #cell(store)="slotData">
        <slot name="cell-store" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          {{ slotData.item.storeName || (slotData.item.store && slotData.item.store.name) || 'Cấp phòng ban' }}
        </slot>
      </template>
      <template #cell(costItem)="slotData">
        <slot name="cell-costItem" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          {{ slotData.item.costItemName || (slotData.item.costItem && slotData.item.costItem.name) || slotData.item.costItemId }}
        </slot>
      </template>
      <template #cell(budgetAllocatedAmount)="slotData">
        <slot name="cell-budgetAllocatedAmount" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          <span v-if="slotData.item.budgetAllocatedAmount != null" class="text-info">
            {{ convertPrice(slotData.item.budgetAllocatedAmount) }}
          </span>
          <span v-else class="text-muted">—</span>
        </slot>
      </template>
      <template #cell(requestedAmount)="slotData">
        <slot name="cell-requestedAmount" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          <strong>{{ convertPrice(slotData.item.requestedAmount) }}</strong>
        </slot>
      </template>
      <template #cell(budgetUsagePeriod)="slotData">
        <slot name="cell-budgetUsagePeriod" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          {{ slotData.item.budgetUsagePeriod || 1 }} tháng
        </slot>
      </template>
      <template #cell(budgetRemainingAfterPR)="slotData">
        <slot name="cell-budgetRemainingAfterPR" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          <span
            v-if="slotData.item.budgetRemainingAfterPR != null"
            :class="slotData.item.budgetRemainingAfterPR < 0 ? 'text-danger' : 'text-success'"
            class="pr-clickable-amount"
            @click="$emit('click-budget', slotData.item)"
          >{{ convertPrice(slotData.item.budgetRemainingAfterPR) }}</span>
          <span v-else class="text-muted">—</span>
        </slot>
      </template>
      <template #cell(actions)="slotData">
        <slot v-if="canDelete" name="cell-actions" v-bind="{ ...slotData, realIndex: pageOffset + slotData.index }">
          <span
            class="flaticon2-rubbish-bin-delete-button text-danger"
            style="cursor:pointer; font-size:1rem"
            @click="onDeleteLine(slotData.item)"
          />
        </slot>
      </template>

      <template v-if="lines.length > 0" #bottom-row>
        <b-td colspan="4" class="text-right font-weight-bold">Tổng cộng:</b-td>
        <b-td class="text-right font-weight-bold text-primary">
          {{ convertPrice(totalAmount || requestedAmount) }}
        </b-td>
        <b-td colspan="3"></b-td>
      </template>
    </b-table>

    <div v-if="lines.length > perPage" class="d-flex justify-content-end mb-1">
      <b-pagination
        v-model="currentPage"
        :total-rows="lines.length"
        :per-page="perPage"
        size="sm"
        class="custom-pagination"
      />
    </div>
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';
import { showDeleteAlert } from '@/utils/sweet-alert2';

export default {
  name: 'PurchaseRequestDetails',
  props: {
    lines: {
      type: Array,
      default: () => [],
    },
    totalAmount: {
      type: Number,
      default: null,
    },
    requestedAmount: {
      type: Number,
      default: null,
    },
    tbodyTrClass: {
      type: [Function, String],
      default: null,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      lineFields: [
        { key: 'lineNumber', label: 'Dòng', thStyle: { width: '60px' }, class: 'text-center' },
        { key: 'store', label: 'Cửa hàng' },
        { key: 'costItem', label: 'Khoản mục phí' },
        { key: 'budgetAllocatedAmount', label: 'NS tháng (đã duyệt)', class: 'text-right' },
        { key: 'requestedAmount', label: 'Số tiền (VNĐ)', class: 'text-right' },
        { key: 'budgetUsagePeriod', label: 'Số tháng SD', thStyle: { width: '100px' }, class: 'text-center' },
        { key: 'budgetRemainingAfterPR', label: 'NS còn lại sau PR', class: 'text-right' },
        { key: 'actions', label: '', thStyle: { width: '50px' }, class: 'text-center' },
      ],
    };
  },
  computed: {
    pageOffset() {
      return (this.currentPage - 1) * this.perPage;
    },
  },
  watch: {
    'lines.length'(newLen, oldLen) {
      const lastPage = Math.ceil(newLen / this.perPage) || 1;
      if (newLen > oldLen) {
        this.currentPage = lastPage;
      } else if (this.currentPage > lastPage) {
        this.currentPage = lastPage;
      }
    },
  },
  methods: {
    convertPrice,
    onDeleteLine(item) {
      const lineLabel = item.costItemName || (item.costItem && item.costItem.name) || `dòng ${item.lineNumber}`;
      showDeleteAlert(
        'Xoá dòng khoản mục phí!',
        `Bạn có chắc muốn xoá ${lineLabel}?`,
        item,
        (deletedItem) => this.$emit('delete-line', deletedItem),
      );
    },
  },
};
</script>

<style scoped>
.pr-clickable-amount {
  cursor: pointer;
  text-decoration: underline dotted;
}
.pr-clickable-amount:hover {
  opacity: 0.75;
}
</style>
